import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import Can from '../../components/Can';
import ConfirmModal from '../../components/ConfirmModal';
import './Ledgers.css';

const TYPES = ['Asset', 'Liability', 'Capital', 'Income', 'Expense'];

const EMPTY = {
  code: '', name: '', ledger_type: 'Expense', sub_group: '',
  opening_balance: '', opening_date: today(), notes: '',
};

export default function Ledgers() {
  const { t: tr } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [msg, setMsg] = useState('');

  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const { data, loading, error, reload } = useApi(
    () => api.ledgers.list(filterType || null), [filterType], { rows: [], totals: {} }
  );

  const filtered = useMemo(() => {
    const rows = data.rows || [];
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.code || '').toLowerCase().includes(q) ||
      (r.sub_group || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const startEdit = (l) => {
    setForm({
      code: l.code, name: l.name, ledger_type: l.ledger_type,
      sub_group: l.sub_group || '',
      opening_balance: l.opening_balance || '',
      opening_date: l.opening_date || today(),
      notes: l.notes || '',
    });
    setEditId(l.ledger_id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { notify('❌ ' + tr('ledgers.errors.nameRequired')); return; }
    if (!form.ledger_type) { notify('❌ ' + tr('ledgers.errors.typeRequired')); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        opening_balance: parseFloat(form.opening_balance || 0),
      };
      if (editId) await api.ledgers.update(editId, payload);
      else        await api.ledgers.create(payload);
      notify(editId ? '✅ ' + tr('ledgers.toasts.updated') : '✅ ' + tr('ledgers.toasts.created'));
      setShowForm(false); setForm(EMPTY); setEditId(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.ledgers.remove(confirm.ledger_id);
      notify('✅ ' + tr('ledgers.toasts.removed'));
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  return (
    <div className="lg-page">
      <div className="page-header">
        <h2 className="page-title">{tr('ledgers.title')}</h2>
        <Can module="accounting" action="create">
          <button className="btn btn-primary btn-sm"
                  onClick={() => { setShowForm(!showForm); setForm(EMPTY); setEditId(null); }}>
            {showForm ? '✕ ' + tr('common.cancel') : '＋ ' + tr('ledgers.newLedger')}
          </button>
        </Can>
      </div>

      {msg && <div className="lg-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      {/* Type summary */}
      <div className="lg-summary">
        {TYPES.map(t => {
          const tot = (data.totals && data.totals[t]) || { count: 0, closing: 0 };
          const tone = ({Asset:'green', Liability:'orange', Capital:'blue', Income:'green', Expense:'red'})[t];
          return (
            <button key={t}
                    className={`lg-stat lg-stat-${tone} ${filterType === t ? 'active' : ''}`}
                    onClick={() => setFilterType(filterType === t ? '' : t)}>
              <div className="lg-stat-label">{tr('ledgers.types.' + t)}</div>
              <div className="lg-stat-count">{tot.count} {tr(tot.count !== 1 ? 'ledgers.ledgersWord' : 'ledgers.ledgerWord')}</div>
              <div className="lg-stat-amount">
                ₹ {fmtNum(tot.closing)}
              </div>
            </button>
          );
        })}
      </div>

      {/* Form */}
      {showForm && (
        <div className="section-card">
          <div className="section-header">{editId ? tr('ledgers.editLedger') : tr('ledgers.newLedger')}</div>
          <div className="lg-form-grid">
            <div className="form-group">
              <label>{tr('common.name')} *</label>
              <input value={form.name}
                     onChange={e => setForm({ ...form, name: e.target.value })}
                     placeholder={tr('ledgers.namePlaceholder')} />
            </div>
            <div className="form-group">
              <label>{tr('common.type')} *</label>
              <select value={form.ledger_type}
                      onChange={e => setForm({ ...form, ledger_type: e.target.value })}>
                {TYPES.map(t => <option key={t} value={t}>{tr('ledgers.types.' + t)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{tr('ledgers.subGroup')}</label>
              <input value={form.sub_group}
                     onChange={e => setForm({ ...form, sub_group: e.target.value })}
                     placeholder={tr('ledgers.subGroupPlaceholder')} />
            </div>
            <div className="form-group">
              <label>{tr('ledgers.codeOptional')}</label>
              <input value={form.code} disabled={!!editId}
                     onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                     placeholder={tr('ledgers.codePlaceholder')} />
            </div>
            <div className="form-group">
              <label>{tr('ledgers.openingBalance')}</label>
              <input type="number" step="0.01" value={form.opening_balance}
                     onChange={e => setForm({ ...form, opening_balance: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{tr('ledgers.openingDate')}</label>
              <input type="date" value={form.opening_date}
                     onChange={e => setForm({ ...form, opening_date: e.target.value })} />
            </div>
            <div className="form-group lg-wide">
              <label>{tr('common.notes')}</label>
              <input value={form.notes}
                     onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <p className="lg-hint">
            💡 {tr('ledgers.hintIntro')} <strong>{tr('ledgers.hintPositive')}</strong> {tr('ledgers.hintDr')}
            <strong> {tr('ledgers.hintPositive')}</strong> {tr('ledgers.hintCr')}
          </p>
          <div style={{ padding: '0 12px 12px' }}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
              {saving ? '⏳ ' + tr('common.saving') : (editId ? tr('common.update') : tr('ledgers.createLedger'))}
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="lg-toolbar">
        <input className="lg-search" type="search" placeholder={'🔍 ' + tr('ledgers.searchPlaceholder')}
               value={search} onChange={e => setSearch(e.target.value)} />
        {filterType && (
          <button className="btn btn-sm" onClick={() => setFilterType('')}>
            ✕ {tr('ledgers.clearFilter')} ({tr('ledgers.types.' + filterType)})
          </button>
        )}
      </div>

      {/* List */}
      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">
            {tr('ledgers.ledgersHeading')} ({filtered.length}{filtered.length !== (data.rows||[]).length ? ` ${tr('ledgers.ofCount', { n: (data.rows||[]).length })}` : ''})
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>{tr('common.code')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.name')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.type')}</th>
                <th style={{ textAlign: 'left' }}>{tr('ledgers.subGroup')}</th>
                <th style={{ textAlign: 'right' }}>{tr('common.opening')}</th>
                <th style={{ textAlign: 'right' }}>{tr('common.closing')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                  {tr('ledgers.noMatch')}
                </td></tr>
              ) : filtered.map(l => (
                <tr key={l.ledger_id}
                    style={{ opacity: Number(l.is_active) === 0 ? 0.5 : 1, cursor: 'pointer' }}
                    onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: { page: 'LedgerView', ledgerId: l.ledger_id } }))}>
                  <td style={{ textAlign: 'left', fontFamily: 'monospace', fontWeight: 600 }}>{l.code}</td>
                  <td style={{ textAlign: 'left' }}>
                    <span style={{ textDecoration: Number(l.is_active) === 0 ? 'line-through' : 'none' }}>
                      {l.name}
                    </span>
                    {Number(l.is_system) === 1 && (
                      <span className="lg-system-pill">{tr('ledgers.systemPill')}</span>
                    )}
                    {Number(l.is_active) === 0 && (
                      <span className="lg-deleted-pill">{tr('ledgers.deletedPill')}</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'left' }}>
                    <span className={`lg-type-pill lg-type-${l.ledger_type.toLowerCase()}`}>{l.ledger_type}</span>
                  </td>
                  <td style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {l.sub_group || '—'}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>
                    {Number(l.opening_balance) !== 0
                      ? `₹${fmtNum(l.opening_balance)}`
                      : '—'}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700,
                               color: Number(l.closing_balance) < 0 ? 'var(--danger)' : 'inherit' }}>
                    ₹ {fmtNum(l.closing_balance)}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <Can module="accounting" action="update">
                      <button className="btn btn-sm" onClick={(e) => {
                        e.stopPropagation();
                        startEdit(l);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}>✏️</button>
                    </Can>
                    {Number(l.is_system) === 0 && (
                      <Can module="accounting" action="delete">
                        <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirm(l);
                                }}>🗑</button>
                      </Can>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title={tr('ledgers.removeTitle')}
        message={confirm
          ? tr('ledgers.removeMsg', { name: confirm.name })
          : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel={tr('ledgers.remove')}
      />
    </div>
  );
}
